import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { adminAuth, STATUS_LABELS, euros } from "@/lib/ceda";
import { AdminCedaCapsuleForm } from "@/components/ceda/AdminCedaCapsuleForm";
import { AdminCedaPacks } from "@/components/ceda/AdminCedaPacks";

const err = (e, m) => toast.error(e.response?.data?.detail || m);
const STATUS_TONE = { draft: "bg-[#E8DCC4] text-[#1F2A24]/60", ready: "bg-[#C9A24B]/20 text-[#8a6a22]", published: "bg-[#385E3A]/15 text-[#385E3A]" };

export default function AdminCeda() {
  const [settings, setSettings] = useState(null);
  const [families, setFamilies] = useState([]);
  const [capsules, setCapsules] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => Promise.all([
    api.get("/admin/ceda/settings", adminAuth()), api.get("/admin/ceda/families", adminAuth()), api.get("/admin/ceda/capsules", adminAuth()),
  ]).then(([s, f, c]) => { setSettings(s.data); setFamilies(f.data); setCapsules(c.data); });
  useEffect(() => { load(); }, []);

  const toggle = async () => {
    const next = !settings.active;
    if (!window.confirm(next ? "Rendre le module CEDA visible du public ?" : "Masquer le module CEDA au public ?")) return;
    const r = await api.put("/admin/ceda/settings", { active: next }, adminAuth());
    setSettings({ ...settings, active: r.data.active });
    toast.success(r.data.active ? "Module CEDA visible" : "Module CEDA masqué");
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer ${c.code || c.title} ?`)) return;
    try { await api.delete(`/admin/ceda/capsules/${c.id}`, adminAuth()); toast.success("Capsule supprimée"); load(); } catch (e) { err(e, "Suppression impossible"); }
  };

  if (!settings) return <p className="font-sans text-sm text-[#1F2A24]/60">Chargement…</p>;
  const famName = (id) => families.find((f) => f.id === id)?.name || "—";

  return (
    <div data-testid="admin-ceda" className="space-y-10">
      <div className={`rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 border-2 ${settings.active ? "border-[#385E3A] bg-[#385E3A]/5" : "border-[#E8DCC4] bg-white"}`}>
        <div>
          <p className="font-serif text-xl text-[#1F2A24]">Module CEDA</p>
          <p className="font-sans text-sm text-[#1F2A24]/65 mt-1" data-testid="admin-ceda-state">
            {settings.active ? "Visible du public : boutique /ceda et menu actifs." : "Masqué : aucune page CEDA n'est visible du public."}
          </p>
          <p className="font-sans text-xs text-[#1F2A24]/50 mt-1">Tarifs : capsule {euros(settings.prices.ceda_single)} · pack de 3 {euros(settings.prices.ceda_pack3)} · pack de 7 {euros(settings.prices.ceda_pack7)} · {settings.active_days} jours après activation</p>
        </div>
        <button data-testid="admin-ceda-toggle" onClick={toggle} className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-ui text-sm transition-colors ${settings.active ? "bg-[#A63B36] text-white hover:bg-[#8d322e]" : "bg-[#385E3A] text-white hover:bg-[#2c4a2e]"}`}>
          <Power size={15} /> {settings.active ? "Masquer le module" : "Activer le module"}
        </button>
      </div>

      <Families families={families} capsules={capsules} reload={load} />

      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl text-[#1F2A24]">Capsules</h3>
          <button data-testid="admin-ceda-add-capsule" onClick={() => setEditing({})} className="inline-flex items-center gap-2 rounded-full bg-[#385E3A] text-white px-5 py-2.5 font-ui text-sm hover:bg-[#2c4a2e]"><Plus size={16} /> Nouvelle capsule</button>
        </div>
        <div className="overflow-x-auto rounded-xl border border-[#E8DCC4] bg-white">
          <table className="w-full text-sm" data-testid="admin-ceda-capsules-table">
            <thead className="bg-[#F6F2E9] text-[#1F2A24]/70 font-ui text-xs">
              <tr>{["Code", "Titre", "Famille", "Média", "PDF", "Statut", ""].map((h) => <th key={h} className="px-3 py-2 text-left">{h}</th>)}</tr>
            </thead>
            <tbody>
              {capsules.length === 0 && <tr><td colSpan={7} className="px-3 py-6 text-center text-[#1F2A24]/50">Aucune capsule. Cliquez « Nouvelle capsule ».</td></tr>}
              {capsules.map((c) => (
                <tr key={c.id} className="border-t" data-testid={`admin-ceda-row-${c.id}`}>
                  <td className="px-3 py-2 font-ui">{c.code || "—"}</td>
                  <td className="px-3 py-2">{c.title}</td>
                  <td className="px-3 py-2">{famName(c.family_id)}</td>
                  <td className="px-3 py-2">{c.has_media ? c.media_type : <span className="text-[#A63B36]">aucun</span>}</td>
                  <td className="px-3 py-2">{c.has_pdf ? "oui" : "—"}</td>
                  <td className="px-3 py-2"><span className={`rounded-full px-2.5 py-1 text-[11px] font-ui ${STATUS_TONE[c.status]}`} data-testid={`admin-ceda-status-${c.id}`}>{STATUS_LABELS[c.status]}</span></td>
                  <td className="px-3 py-2 flex gap-1 justify-end">
                    <button onClick={() => setEditing(c)} data-testid={`admin-ceda-edit-${c.id}`} className="p-1.5 rounded border"><Pencil size={14} /></button>
                    <button onClick={() => remove(c)} data-testid={`admin-ceda-delete-${c.id}`} className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AdminCedaPacks capsules={capsules} />

      {editing && <AdminCedaCapsuleForm initial={editing} families={families} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

const Families = ({ families, capsules, reload }) => {
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      const body = { ...edit, order: Number(edit.order) || 0 };
      if (edit.id) await api.put(`/admin/ceda/families/${edit.id}`, body, adminAuth());
      else await api.post("/admin/ceda/families", body, adminAuth());
      toast.success("Famille enregistrée"); setEdit(null); reload();
    } catch (e) { err(e, "Enregistrement impossible"); }
  };
  const del = async (f) => {
    if (!window.confirm(`Supprimer la famille ${f.name} ?`)) return;
    try { await api.delete(`/admin/ceda/families/${f.id}`, adminAuth()); reload(); } catch (e) { err(e, "Suppression impossible"); }
  };
  return (
    <section data-testid="admin-ceda-families">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-2xl text-[#1F2A24]">Familles</h3>
        <button data-testid="admin-ceda-add-family" onClick={() => setEdit({ name: "", code_range: "", description: "", order: families.length + 1 })} className="inline-flex items-center gap-2 rounded-full border border-[#385E3A] text-[#385E3A] px-4 py-2 font-ui text-sm"><Plus size={15} /> Famille</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {families.map((f) => edit?.id === f.id ? null : (
          <div key={f.id} className="rounded-xl bg-white border border-[#E8DCC4] p-4 flex items-start justify-between gap-3" data-testid={`admin-ceda-family-${f.id}`}>
            <div>
              <p className="font-serif text-lg text-[#1F2A24]">{f.name}</p>
              <p className="font-ui text-xs text-[#C9A24B]">{f.code_range} · {capsules.filter((c) => c.family_id === f.id).length} capsule(s)</p>
              {f.description && <p className="font-sans text-xs text-[#1F2A24]/60 mt-1">{f.description}</p>}
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEdit(f)} data-testid={`admin-ceda-family-edit-${f.id}`} className="p-1.5 rounded border"><Pencil size={13} /></button>
              <button onClick={() => del(f)} className="p-1.5 rounded border text-[#A63B36]"><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {edit && (
          <div className="rounded-xl bg-white border-2 border-[#C9A24B] p-4 grid gap-2" data-testid="admin-ceda-family-form">
            <input className="border rounded px-3 py-2 text-sm" placeholder="Nom" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="admin-ceda-family-name" />
            <input className="border rounded px-3 py-2 text-sm" placeholder="Codes (ex : CEDA 05–09)" value={edit.code_range} onChange={(e) => setEdit({ ...edit, code_range: e.target.value })} data-testid="admin-ceda-family-codes" />
            <textarea className="border rounded px-3 py-2 text-sm" rows={2} placeholder="Description" value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
            <input className="border rounded px-3 py-2 text-sm w-24" placeholder="Ordre" value={edit.order} onChange={(e) => setEdit({ ...edit, order: e.target.value })} />
            <div className="flex gap-2">
              <button onClick={save} data-testid="admin-ceda-family-save" className="p-2 rounded bg-[#385E3A] text-white"><Check size={14} /></button>
              <button onClick={() => setEdit(null)} className="p-2 rounded border"><X size={14} /></button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
